import { useState } from "react";
import { MapPin, Clock, Phone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DataTable,
  PageHeader,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import {
  useAdvanceDeliveryOrderMutation,
  useDeliveryOrders,
  useDeliveryPartnerId,
} from "@/hooks/queries";
import { getDeliveryOrderAdvanceAction } from "@/lib/mappers/delivery-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import type { DeliveryOrderRow } from "@/types/delivery.ui";
import { useToast } from "@/hooks/use-toast";

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All", value: "all" },
      { label: "Assigned", value: "assigned" },
      { label: "Picked Up", value: "picked_up" },
      { label: "In Transit", value: "in_transit" },
      { label: "Delivered", value: "delivered" },
    ],
  },
];

const statusColor: Record<string, string> = {
  assigned: "bg-info/10 text-info border-info/20",
  picked_up: "bg-warning/10 text-warning border-warning/20",
  in_transit: "bg-accent/10 text-accent border-accent/20",
  delivered: "bg-success/10 text-success border-success/20",
};

const DeliveryOrdersPage = () => {
  const { partnerId, isLoading: partnerLoading } = useDeliveryPartnerId();
  const { data, isLoading: ordersLoading } = useDeliveryOrders(partnerId);
  const advanceMutation = useAdvanceDeliveryOrderMutation(partnerId ?? 0);
  const { toast } = useToast();

  const [otpDialog, setOtpDialog] = useState<{ order: DeliveryOrderRow } | null>(null);
  const [otp, setOtp] = useState("");

  const orders = data?.items ?? [];

  const table = useDataTable<DeliveryOrderRow>({
    data: orders,
    searchKeys: ["orderId", "customerName", "storeName"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const submitAdvance = async (order: DeliveryOrderRow, deliveryOtp?: string) => {
    if (!partnerId) return;
    const action = getDeliveryOrderAdvanceAction(order.status);
    if (!action) return;

    try {
      await advanceMutation.mutateAsync({
        orderId: Number(order.id),
        currentStatus: order.status,
        otp: deliveryOtp,
      });
      toast({ title: "Status updated", description: `${order.orderId} updated` });
      setOtpDialog(null);
      setOtp("");
    } catch (error) {
      toast({
        title: "Failed to update delivery",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleAdvance = (order: DeliveryOrderRow) => {
    if (order.status === "in_transit") {
      setOtp("");
      setOtpDialog({ order });
      return;
    }
    submitAdvance(order);
  };

  const columns: DataTableColumn<DeliveryOrderRow>[] = [
    {
      id: "orderId",
      header: "Order",
      accessorKey: "orderId",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      cell: (order) => (
        <Badge variant="outline" className={statusColor[order.status]}>{order.status.replace("_", " ")}</Badge>
      ),
    },
  ];

  const renderOrderCard = (order: DeliveryOrderRow) => {
    const actionLabel = getDeliveryOrderAdvanceAction(order.status);

    return (
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold">{order.orderId}</p>
              <p className="text-xs text-muted-foreground">{order.orderDate} • {order.items} items • ₹{order.total}</p>
            </div>
            <Badge variant="outline" className={statusColor[order.status]}>{order.status.replace("_", " ")}</Badge>
          </div>

          <div className="space-y-2.5 text-sm">
            <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-secondary/30">
              <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-xs text-primary mb-0.5">PICKUP</p>
                <p className="font-medium">{order.storeName}</p>
                <p className="text-xs text-muted-foreground">{order.storeAddress}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-secondary/30">
              <MapPin className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-xs text-destructive mb-0.5">DROP OFF</p>
                <p className="font-medium">{order.customerName}</p>
                <p className="text-xs text-muted-foreground">{order.customerAddress}</p>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3" /> {order.customerPhone}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{order.distance}</span>
              <span>•</span>
              <span><Clock className="inline h-3 w-3 mr-0.5" />{order.estimatedTime}</span>
              <span>•</span>
              <span className="font-semibold text-success">₹{order.commission}</span>
              {order.paymentMethod === "cod" && order.codAmount && (
                <>
                  <span>•</span>
                  <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-[10px]">
                    Collect ₹{order.codAmount}
                  </Badge>
                </>
              )}
            </div>
            {actionLabel && (
              <Button
                size="sm"
                onClick={() => handleAdvance(order)}
                disabled={advanceMutation.isPending}
              >
                {actionLabel}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (partnerLoading || ordersLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!partnerId) {
    return (
      <PageHeader title="My Deliveries" description="No delivery partner profile linked to your account." />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Deliveries"
        description="Manage your assigned deliveries."
      />

      <DataTable
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        viewMode="cards"
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search deliveries..."
        filters={statusFilters}
        filterValues={table.state.filters}
        onFilterChange={table.setFilter}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        hidePagination={orders.length <= 10}
        renderMobileCard={renderOrderCard}
      />

      <Dialog open={Boolean(otpDialog)} onOpenChange={(open) => !open && setOtpDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="delivery-otp">Enter delivery OTP from customer</Label>
            <Input
              id="delivery-otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit OTP"
              maxLength={6}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOtpDialog(null)}>Cancel</Button>
            <Button
              onClick={() => otpDialog && submitAdvance(otpDialog.order, otp)}
              disabled={!otp || advanceMutation.isPending}
            >
              Confirm Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryOrdersPage;
